<!--
  Own-catalog boolean (`palette_source` ≠ parent): same layout as dictionary unsupported cards
  (title row + compact switch + copy), with primary blue left accent — not parent/teal dictionary stripe.
-->
<script setup lang="ts">
import { computed, withDefaults } from 'vue'
import type { RuntimeFieldValueMap } from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import { useTranslate } from '@/shared/lib'
import type { RuntimeFieldSlot } from '../model/runtimeLayoutAdapter'
import { shouldShowPassportRuntimeValueTooltip } from '../model/passportRuntimeFieldDisplay'
import { runtimePassportFieldLabelFromSlot } from '../model/passportRuntimeFieldLabel'

const props = withDefaults(
  defineProps<{
    item: RuntimeFieldSlot
    fieldValues: RuntimeFieldValueMap
    fieldsLocked?: boolean
    /** When false, value click does not open the all-fields drawer. */
    clickable?: boolean
    /** Подсветка после failed save (обязательное пустое поле). */
    showMandatoryError?: boolean
  }>(),
  {
    clickable: true,
    showMandatoryError: false,
  },
)

const emit = defineEmits<{
  openDrawer: [fieldId: string | null]
  patchField: [payload: { fieldId: string; value: string }]
}>()

const { trs } = useTranslate()

const cellTitle = computed(() => runtimePassportFieldLabelFromSlot(props.item))

const displayText = computed(() => {
  const raw = props.fieldValues[props.item.fieldId]
  if (raw === undefined || raw === null || raw === '') {
    return trs('itf.not_set')
  }
  return String(raw)
})

const showValueTooltip = computed(() =>
  shouldShowPassportRuntimeValueTooltip({
    fieldType: props.item.field.type,
    layoutPaletteSource: props.item.layoutItem.palette_source,
    displayText: displayText.value,
  }),
)

const toBooleanModel = (value: unknown): boolean => {
  if (value === true || value === false) return value
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
  if (normalized === 'false' || normalized === '0') return false
  return true
}

const switchModel = computed({
  get: () => toBooleanModel(props.fieldValues[props.item.fieldId]),
  set: (b: boolean) => {
    emit('patchField', { fieldId: props.item.fieldId, value: String(b) })
  },
})

function onValueClick() {
  if (props.clickable === false) return
  emit('openDrawer', props.item.fieldId)
}
</script>

<template>
  <div
    class="passport-runtime-v2__unsupported-field-card passport-runtime-v2__own-catalog-field-card"
    :class="{
      'passport-runtime-v2__own-catalog-field-card--mandatory-error': showMandatoryError,
    }"
  >
    <div class="passport-runtime-v2__unsupported-field-card__name" :title="cellTitle">
      {{ cellTitle }}
    </div>
    <div class="passport-runtime-v2__unsupported-field-card__row">
      <div class="passport-runtime-v2__unsupported-field-card__value-wrap">
        <VTooltip location="top" :eager="false" :disabled="!showValueTooltip">
          <template #activator="{ props: tipProps }">
            <div
              v-bind="tipProps"
              class="passport-runtime-v2__unsupported-field-card__value passport-runtime-v2__unsupported-field-card__value--boolean-switch"
              :class="{
                'passport-runtime-v2__unsupported-field-card__value--clickable':
                  clickable !== false,
              }"
              role="button"
              tabindex="-1"
              @click="onValueClick"
            >
              <VRadioGroup
                v-model="switchModel"
                inline
                class="passport-runtime-v2__radio"
                color="primary"
                density="compact"
                hide-details
                :readonly="Boolean(fieldsLocked)"
                @click.stop
              >
                <VRadio :value="true" :label="$t('itf.yes')" />
                <VRadio :value="false" :label="$t('itf.no')" />
              </VRadioGroup>
            </div>
          </template>
          <span>{{ displayText }}</span>
        </VTooltip>
      </div>
    </div>
    <div
      v-if="showMandatoryError"
      class="passport-runtime-v2__own-catalog-field-card__error"
    >
      {{ trs('validate.required') }}
    </div>
  </div>
</template>

<style scoped>
.passport-runtime-v2__unsupported-field-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
}

.passport-runtime-v2__unsupported-field-card.passport-runtime-v2__own-catalog-field-card {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.passport-runtime-v2__unsupported-field-card__row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.passport-runtime-v2__unsupported-field-card__value-wrap {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  z-index: 0;
}

.passport-runtime-v2__unsupported-field-card__value {
  flex: 1 1 auto;
  min-width: 0;
}

.passport-runtime-v2__unsupported-field-card__value--clickable {
  cursor: pointer;
}

.passport-runtime-v2__unsupported-field-card__value--boolean-switch {
  display: flex;
  align-items: center;
  min-height: 0;
}

.v-selection-control--density-compact {
  --v-selection-control-size: auto;
}

.passport-runtime-v2__own-catalog-field-card--mandatory-error
  .passport-runtime-v2__unsupported-field-card__name {
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__own-catalog-field-card--mandatory-error
  .passport-runtime-v2__unsupported-field-card__value--boolean-switch {
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 4px;
}

.passport-runtime-v2__own-catalog-field-card__error {
  margin-top: 4px;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}
</style>
